import type { Predicate } from '../../lib/predicatedProcessor';
import { Operation } from '../../types';
import type { Change } from '../../types';
import type { RuleChangeExistence, TSuiteBufferEntry } from './types';
import Predicates from './predicates';
import type createTemplates from './templates';


export function buildIneligibleChangeEntry({
  actionPublicId, templates,
}: {
  actionPublicId: string;
  templates: ReturnType<typeof createTemplates>;
}): TSuiteBufferEntry {
  return {
    actionPublicId,
    line: templates.ineligibleChange(),
    isIneligible: true,
  };
}

export function prependWithIneligibleChange({
  entries, templates,
}: {
  entries: TSuiteBufferEntry | TSuiteBufferEntry[];
  templates: ReturnType<typeof createTemplates>;
}) {
  const arr = Array.isArray(entries) ? entries : [entries];

  const actionPublicIdsSet = new Set<string>(arr.map(e => e.actionPublicId));
  /**
   * we defend against having entries belonging to different actions, since adding an ineligible
   * change to mixed entries would result in un-predicable behavior
   */
  if (actionPublicIdsSet.size !== 1) {
    throw new Error('Entries should only belong to the same action');
  }

  const actionPublicId = [...actionPublicIdsSet][0];

  return [
    buildIneligibleChangeEntry({ actionPublicId, templates }),
    ...arr,
  ];
}
/**
 * Builds a record containing the existence of rule-related changes per action id.
 * The result of this is used for various memoizations
 */
export function getChangeExistenceForRuleChangesByActionId(
  changes: Change[],
): Record<string, RuleChangeExistence> {
  const changeExistenceByActionId = changes.reduce((changesMap, change) => {
    const { actionPublicId, descriptor, op } = change;

    if (!Object.hasOwn(changesMap, actionPublicId)) {
      changesMap[actionPublicId] = {
        mlaOperations: {
          [Operation.Add]: false,
          [Operation.Modify]: false,
          [Operation.Remove]: false,
        },
        rulesModified: false,
      };
    }

    if (descriptor === 'threshold' || descriptor === 'action.rules.grouping') {
      changesMap[actionPublicId].rulesModified = true;
    }

    if (descriptor === 'action.rules.mla') {
      changesMap[actionPublicId].mlaOperations[op] = true;
    }

    return changesMap;
  }, {} as Record<string, RuleChangeExistence>);

  return changeExistenceByActionId;
}

/**
 * Filters out the unsupported changes by the testing suite formatter.
 * @param changes all changes detected in the WF version
 * @returns a sub-array of only the supported changes
 */
export function filterUnsupportedChanges(changes: Change[]): Change[] {
  /**
   * These are the predicates that are relevant for the testing suite formatter.
   * A change must meet at least one of these in order to be preserved.
   */
  const relevantPredicates: Predicate<Change>[] = [
    Predicates.actionAdded,
    Predicates.actionRemoved,
    Predicates.inputMetadataModified,
    Predicates.matrixMetadataModified,
    Predicates.outcomeMetadataModified,
    Predicates.outputExpressionModified,
    Predicates.outputMetadataModified,
    Predicates.addOrModifyRuleChangesForMatrices,
    Predicates.addOrModifyRuleChangesForOutcomes,
    Predicates.addOrModifyRuleChangesForOutputs,
    Predicates.addOrModifyRuleChangesForServices,
    Predicates.addOrModifyRuleChangesForTags,
    Predicates.actionMLAAdded,
  ];

  // only keep the changes that meet at least one predicate.
  return changes.filter(change => relevantPredicates.some(predicate => predicate(change)));
}
